import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';

import DataTable from '../../juristec-ui/core/DataTable';
import { formatDateTime } from '../../juristec-ui/utils/functions/lab';
import Tooltip from '../../juristec-ui/core/Tooltip';
import Badge from '../../juristec-ui/core/Badge';
import DataCards from '../../juristec-ui/core/DataCards';
import IconButton from '../../juristec-ui/core/IconButton';
import TablePagination from '../../juristec-ui/core/TablePagination';
import TableCellPop from '../../juristec-ui/core/TableCellPop';

import {
  UserConfig,
  History,
  Dashboard,
  UserAtom,
  Users,
  Question,
  ProfileUser,
  Trash,
  UserReactivate,
  UserDeactivate,
  AtSign,
  OpenIdConnect,
  Support,
} from '../../juristec-ui/icons';

import useDeviceType from '../../juristec-ui/hooks/useDeviceType';

import {
  UserInfoText,
  Options,
  Table,
  UsersTableOverFlow,
  BadgeContent,
  IconWrapper,
  CompanyInfoContainer,
} from './styled/UsersTable.styled';
import Unlock from '../../juristec-ui/icons/Unlock';
import TableFilters from '../Popovers/TableFilters';

/**
 * A component that displays the users info and options
 */
const UsersTable = ({
  users,
  handleDelete = () => {},
  getUsers = () => {},
  getUserInfo = () => {},
  getUserHistory = () => {},
  getUserDashboards = () => {},
  handleDeactivate = () => {},
  handleReactivateUser = () => {},
  filterText = '',
  userIsAdmin = false,
  userIsSupport = false,
  handleGetResetPwdLink = () => {},
  handleUserEmail = () => {},
}) => {
  const device = useDeviceType();
  const theme = useTheme();

  const [data, setData] = useState(users);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    email: { isOpen: false, selected: [], isDirty: false },
    companyName: { isOpen: false, selected: [], isDirty: false },
    role: { isOpen: false, selected: [], isDirty: false },
  });

  const appliedFiltersUsers = useMemo(() => data.filter((u) => {
    const emailFilter = filters.email.selected;
    const companyFilter = filters.companyName.selected;
    const roleFilter = filters.role.selected;

    const matchesEmail = emailFilter.length === 0 || emailFilter.includes(u.email);

    const matchesCompany = companyFilter.length === 0 || companyFilter.includes(u.companyName);

    const matchesRole = roleFilter.length === 0
      || roleFilter.includes(u.role);

    return (
      matchesEmail && matchesCompany && matchesRole
    );
  }), [data, filters]);

  const instancesToShowToSupport = [
    'Operacional',
    'Preparado',
    'Prospecto',
    'Em implantação',
    'Em espera',
  ];

  const DataListingComponent = device === 'phone' ? DataCards : DataTable;

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filterText]);

  useEffect(() => {
    let filteredUsers = users;
    // filtro de suporte
    if (userIsSupport) {
      filteredUsers = filteredUsers.filter(
        (u) => instancesToShowToSupport.includes(u?.company?.operationalStatus),
      );
    }
    // filtro de busca
    if (filterText) {
      const value = filterText.toLowerCase();

      filteredUsers = filteredUsers.filter((user) => (
        user?.displayName && user?.displayName.toLowerCase().includes(value))
        || (user?.email && user?.email.toLowerCase().includes(value)));
    }

    setData(filteredUsers);
  }, [users, userIsSupport, filterText]);

  const getUsersFromSameCompany = (userCompanyId) => users.filter(
    (user) => user?.company?.id === userCompanyId,
  );

  const UserOptions = ({ user }) => {
    const getInfo = (e) => {
      e.stopPropagation();
      getUserInfo(user);
    };
    const getHistory = (e) => {
      e.stopPropagation();
      getUserHistory(user);
    };
    const getDashboards = (e) => {
      const sameCompanyUsers = getUsersFromSameCompany(user?.company?.id);
      e.stopPropagation();
      getUserDashboards(user, sameCompanyUsers);
    };

    const deleteUser = (e) => {
      e.stopPropagation();
      const { handler, modalHandler } = handleDelete(user);

      const aux = () => {
        setData((p) => p.filter((u) => u.uid !== user.uid));
      };

      const handlerWrapper = () => handler(aux);
      modalHandler(handlerWrapper);
    };

    const deactivateUser = (e) => {
      e.stopPropagation();
      handleDeactivate(user);
    };

    const reactivateUser = (e) => {
      e.stopPropagation();
      const { handler, modalHandler } = handleReactivateUser(user);

      const aux = (d) => {
        setData((p) => p.map((u) => (u.uid === user.uid ? { ...u, removed: d } : u)));
      };

      const handlerWrapper = () => handler(aux);
      modalHandler(handlerWrapper);
    };

    const getResetPwdLink = (e) => {
      e.stopPropagation();
      handleGetResetPwdLink(user);
    };

    const openUserEmail = (e) => {
      e.stopPropagation();
      const { submitNewEmail, modalHandler } = handleUserEmail(user);

      const aux = (newEmail) => {
        setData((p) => p.map((u) => (u.uid === user.uid ? { ...u, email: newEmail } : u)));
      };

      const handlerWrapper = (newEmail) => submitNewEmail(newEmail, aux);
      modalHandler(handlerWrapper);
    };

    return (
      <Options>
        <Tooltip text="Informações">
          <IconButton variant="pattern" shape="rounded" color="primary" onClick={getInfo}>
            <ProfileUser />
          </IconButton>
        </Tooltip>
        <Tooltip text="Acessos">
          <IconButton variant="pattern" shape="rounded" color="primary" onClick={getHistory}>
            <History />
          </IconButton>
        </Tooltip>
        <Tooltip text="Dashboards">
          <IconButton variant="pattern" shape="rounded" color="primary" onClick={getDashboards}>
            <Dashboard />
          </IconButton>
        </Tooltip>
        {(userIsSupport || userIsAdmin) && (
          <>
            {user.login_providers?.includes('password') && (
              <>
                <Tooltip text="Gerar link de reset de senha">
                  <IconButton
                    variant="pattern"
                    shape="rounded"
                    color="primary"
                    onClick={getResetPwdLink}
                    disabled={user?.removed}
                  >
                    <Unlock />
                  </IconButton>
                </Tooltip>
                <Tooltip text="Gerenciar e-mail">
                  <IconButton
                    variant="pattern"
                    shape="rounded"
                    color="primary"
                    onClick={openUserEmail}
                    disabled={user?.removed}
                  >
                    <AtSign />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {user?.removed ? (
              <>
                <Tooltip text="Restaurar usuário">
                  <IconButton variant="pattern" shape="rounded" color="primary" onClick={reactivateUser}>
                    <UserReactivate />
                  </IconButton>
                </Tooltip>
                {userIsAdmin && (
                  <Tooltip text="Apagar usuário completamente">
                    <IconButton variant="pattern" shape="rounded" color="primary" onClick={deleteUser}>
                      <Trash />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : (
              <Tooltip text="Remover usuário">
                <IconButton variant="pattern" shape="rounded" color="primary" onClick={deactivateUser}>
                  <UserDeactivate />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Options>
    );
  };

  UserOptions.propTypes = {
    user: PropTypes.shape({
      company: PropTypes.shape({
        id: PropTypes.string,
      }),
      user_id: PropTypes.string,
      removed: PropTypes.bool,
      uid: PropTypes.string,
      login_providers: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
  };

  /* eslint-disable react/destructuring-assignment */
  const UserInfo = (user) => (
    <UserInfoText>
      <h4>{user?.displayName || user?.email.split('@')[0]}</h4>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <TableCellPop text={user?.email} />
        {user?.company?.loginProvider?.length > 0 && (
          <Tooltip text="Usuário SSO">
            <OpenIdConnect fill={theme.info} />
          </Tooltip>
        )}
      </div>
      {user.removed && (
        <>
          <span className="removed">
            <strong style={{ color: theme.error }}>Removido por: </strong>
            {user?.removedByEmail || 'Usuário não encontrado'}
          </span>
          <span className="removed">
            <strong style={{ color: theme.error }}>Removido em: </strong>
            {user?.removedAt || 'Data não encontrada'}
          </span>
        </>
      )}
    </UserInfoText>
  );
  /* eslint-enable react/destructuring-assignment */

  UserInfo.propTypes = {
    user: PropTypes.shape({
      displayName: PropTypes.string,
      email: PropTypes.string,
      removed: PropTypes.bool,
      removedByEmail: PropTypes.string,
      removedAt: PropTypes.string,
    }).isRequired,
  };

  const formatDate = (datetime) => (
    datetime ? formatDateTime(datetime, { time: 'none' }) : 'Não consta'
  );

  const formatRole = (role) => {
    let conditionalColor = 'error';
    let icon = <Question height="20px" />;
    let userType = 'Desconhecido';
    switch (role) {
      case 'support':
        conditionalColor = 'info';
        icon = <Support width="20px" />;
        userType = 'Suporte';
        break;
      case 'super':
        conditionalColor = 'primary';
        icon = <UserConfig width="20px" />;
        userType = 'Proprietário';
        break;
      case 'scientist':
        conditionalColor = 'success';
        icon = <UserAtom width="20px" />;
        userType = 'Cientista';
        break;
      case 'guest':
        conditionalColor = 'warning';
        icon = <Users width="20px" />;
        userType = 'Convidado';
        break;
      default:
        break;
    }
    return (
      <Badge color={conditionalColor} style={{ margin: 'auto' }}>
        <BadgeContent>
          <IconWrapper color={conditionalColor}>
            {icon}
          </IconWrapper>
          {userType}
        </BadgeContent>
      </Badge>
    );
  };

  const companyInfo = (param) => {
    let conditionalColor = 'transparent';
    switch (param?.company?.operationalStatus) {
      case 'Em implantação':
      case 'Em espera':
      case 'Atenção':
        conditionalColor = 'warning';
        break;
      case 'Suspenso':
      case 'Cancelado':
        conditionalColor = 'error';
        break;
      case 'Teste':
      case 'Comercial':
      case 'Prospecto':
        conditionalColor = 'info';
        break;
      case 'Preparado':
      case 'Operacional':
        conditionalColor = 'success';
        break;
      default:
        conditionalColor = 'info';
    }
    if (param?.company?.operationalStatus) {
      return (
        <CompanyInfoContainer>
          <span style={{ marginBottom: '5px' }}>
            {param.companyName}
            {param?.company?.loginProvider?.length > 0 && (
              <Tooltip text="Cliente SSO">
                <OpenIdConnect fill={theme.info} />
              </Tooltip>
            )}
          </span>
          {param?.company?.operationalStatus && (
            <Badge size="small" color={conditionalColor} style={{ margin: 'auto' }}>
              {param?.company?.operationalStatus}
            </Badge>
          )}
        </CompanyInfoContainer>
      );
    }
    return null;
  };

  const toggleFilter = (field, state) => {
    setFilters((f) => ({
      ...f,
      [field]: {
        ...f[field],
        isOpen: state !== undefined ? state : !f[field].isOpen,
      },
    }));
  };

  const handleFilter = (field, selected) => {
    setFilters((f) => ({
      ...f,
      [field]: {
        ...f[field],
        selected,
        isDirty: true,
      },
    }));
  };

  const emailOptionsFilter = useMemo(() => (
    [...new Set(data.map((u) => u.email))]
      .map((email) => ({
        id: email,
        label: email,
        value: email,
      }))
  ), [data]);

  const companyOptionsFilter = useMemo(() => (
    [...new Set(data.map((u) => u.companyName))]
      .map((company) => {
        if (company === '' || company === undefined || company === null) {
          return {
            id: '',
            label: '-',
            value: '',
          };
        }
        return {
          id: company,
          label: company,
          value: company,
        };
      })
  ), [data]);

  const roleOptionsFilter = useMemo(() => {
    const possibleRoles = {
      support: { id: 'support', label: 'Suporte' },
      super: { id: 'super', label: 'Proprietário' },
      scientist: { id: 'scientist', label: 'Cientista' },
      guest: { id: 'guest', label: 'Convidado' },
    };

    return [...new Set(data.map((u) => u.role))]
      .map((role) => {
        if (role === '' || role === undefined || role === null) {
          return {
            id: '',
            label: 'Desconhecido',
            value: '',
          };
        }
        return {
          id: possibleRoles[role].id,
          label: possibleRoles[role].label,
          value: possibleRoles[role].id,
        };
      });
  }, [data]);

  const columns = useMemo(() => [
    {
      field: 'email',
      role: 'primaryInfo',
      label: 'Usuário',
      sortable: true,
      valueGetter: (param) => UserInfo(param),
      tools: (
        <TableFilters
          field="email"
          isOpen={filters?.email?.isOpen || false}
          toggle={toggleFilter}
          options={emailOptionsFilter}
          selected={filters?.email?.selected || []}
          handleFilter={handleFilter}
        />
      ),
    },
    {
      field: 'companyName',
      role: 'extraInfo',
      label: 'Cliente',
      sortable: true,
      valueGetter: (param) => companyInfo(param),
      tools: (
        <TableFilters
          field="companyName"
          isOpen={filters?.companyName?.isOpen || false}
          toggle={toggleFilter}
          options={companyOptionsFilter}
          selected={filters?.companyName?.selected || []}
          handleFilter={handleFilter}
        />
      ),
    },
    {
      field: 'role',
      role: 'extraInfo',
      label: 'Permissão',
      sortable: true,
      valueGetter: (param) => formatRole(param?.role),
      tools: (
        <TableFilters
          field="role"
          isOpen={filters?.role?.isOpen || false}
          toggle={toggleFilter}
          options={roleOptionsFilter}
          selected={filters?.role?.selected || []}
          handleFilter={handleFilter}
        />
      ),
    },
    {
      field: 'created_at',
      role: 'extraInfo',
      label: 'Data da criação',
      sortable: true,
      valueGetter: (param) => formatDate(param?.created_at || ''),
    },
    {
      field: 'last_login',
      role: 'extraInfo',
      label: 'Último login',
      sortable: true,
      valueGetter: (param) => formatDate(param?.last_login || ''),
    },
    {
      field: 'options',
      role: 'optionsBtn',
      label: 'Opções',
      valueGetter: (param) => <UserOptions user={param} />,
    },
  ], [data, filters]);

  return (
    <Table>
      <UsersTableOverFlow>
        <DataListingComponent
          columns={columns}
          rowData={appliedFiltersUsers}
          strippedRowsColor={theme.containerOdd}
          defaultSortField="email"
          defaultSortOrder="ascending"
          headerColor="transparent"
          rowColor={theme.tableBackground}
          theadStyle={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: theme.background,
          }}
          usePagination
          page={page}
          itemsPerPage={20}
        />
      </UsersTableOverFlow>
      <TablePagination
        page={page}
        setPage={setPage}
        totalPages={Math.floor(appliedFiltersUsers.length / 20)}
      />
    </Table>
  );
};

UsersTable.propTypes = {
  /**
   * Array with the data of the users
   */
  users: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string,
      email: PropTypes.string,
      company: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        operationalStatus: PropTypes.string,
        loginProvider: PropTypes.string,
      }),
      role: PropTypes.string,
      created_at: PropTypes.string,
      last_login: PropTypes.string,
    }),
  ).isRequired,
  /**
   * Function to retrieve all the users of the project
   */
  getUsers: PropTypes.func,
  /**
   * Function to show the information of an specific user
   */
  getUserInfo: PropTypes.func,
  getUserHistory: PropTypes.func,
  getUserDashboards: PropTypes.func,
  filterText: PropTypes.string,
  handleDelete: PropTypes.func,
  handleDeactivate: PropTypes.func,
  handleReactivateUser: PropTypes.func,
  handleGetResetPwdLink: PropTypes.func,
  userIsAdmin: PropTypes.bool,
  userIsSupport: PropTypes.bool,
};

export default UsersTable;
